import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';

import { CustomSuccessToast } from '@shared/components/customSuccessToast';
import { useErrorHandler } from '@shared/hooks/useErrorHandler';
import type { OidcGroupMappingDto } from '@shared/services/apiService';

import type { AccessControlApi } from '../model/useAccessControlApi';
import type { SsoMappingsApi } from '../model/useSsoMappingsApi';
import { useStyles } from './styles';

interface SsoMappingDialogProps {
  open: boolean;
  /** Set to edit that row; `null` adds a new one. */
  mapping: OidcGroupMappingDto | null;
  api: AccessControlApi;
  sso: SsoMappingsApi;
  onClose: () => void;
}

export const SsoMappingDialog: React.FC<SsoMappingDialogProps> = ({
  open,
  mapping,
  api,
  sso,
  onClose,
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { showError } = useErrorHandler();

  const [connectorId, setConnectorId] = useState('');
  const [groupValue, setGroupValue] = useState('');
  const [permissionGroupId, setPermissionGroupId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    // With a single connector there is nothing to choose, so it starts selected.
    setConnectorId(
      mapping?.connectorId ?? (sso.connectorIds.length === 1 ? sso.connectorIds[0] : ''),
    );
    setGroupValue(mapping?.groupValue ?? '');
    setPermissionGroupId(mapping?.permissionGroup.id ?? '');
  }, [open, mapping, sso.connectorIds]);

  const trimmedGroupValue = groupValue.trim();
  const canSubmit = Boolean(connectorId && trimmedGroupValue && permissionGroupId);

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await sso.saveMapping(
        { connectorId, groupValue: trimmedGroupValue, permissionGroupId },
        mapping?.id,
      );
      toast.success(CustomSuccessToast, {
        closeButton: false,
        autoClose: 4000,
        icon: false,
        data: {
          title: t('accessControl.ssoMappings.toast.savedTitle'),
          description: t('accessControl.ssoMappings.toast.saved', { group: trimmedGroupValue }),
        },
      });
      onClose();
    } catch (error) {
      showError({ title: t('accessControl.ssoMappings.errors.saveTitle'), error });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: classes.modalPaper }}>
      <DialogTitle>
        {t(
          mapping
            ? 'accessControl.ssoMappings.dialog.editTitle'
            : 'accessControl.ssoMappings.dialog.addTitle',
        )}
      </DialogTitle>
      <DialogContent dividers className={classes.modalContent}>
        <TextField
          select
          label={t('accessControl.ssoMappings.dialog.connector')}
          value={connectorId}
          onChange={(event) => setConnectorId(event.target.value)}
          disabled={sso.connectorIds.length === 1}
          helperText={t('accessControl.ssoMappings.dialog.connectorHint')}
          data-testid="sso-mapping-connector">
          {sso.connectorIds.map((id) => (
            <MenuItem key={id} value={id}>
              {id}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label={t('accessControl.ssoMappings.dialog.groupValue')}
          value={groupValue}
          onChange={(event) => setGroupValue(event.target.value)}
          helperText={t('accessControl.ssoMappings.dialog.groupValueHint')}
          inputProps={{ maxLength: 1024 }}
          data-testid="sso-mapping-group-value"
        />
        <TextField
          select
          label={t('accessControl.ssoMappings.dialog.permissionGroup')}
          value={permissionGroupId}
          onChange={(event) => setPermissionGroupId(event.target.value)}
          helperText={t('accessControl.ssoMappings.dialog.permissionGroupHint')}
          data-testid="sso-mapping-permission-group">
          {api.groups.map((group) => (
            <MenuItem key={group.id} value={group.id}>
              {group.builtIn ? `${group.name} (${t('accessControl.builtIn')})` : group.name}
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions className={classes.modalActions}>
        <Button onClick={onClose} disabled={isSubmitting}>
          {t('accessControl.common.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit || isSubmitting}
          data-testid="sso-mapping-save">
          {isSubmitting ? t('accessControl.common.saving') : t('accessControl.common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
